// This file contains logic around fetching media download URLs using artifact references.
// It's wrapped with a custom caching/deduping layer.
// Similar to `./vatching/existingFilePaths.ts` and `./batching/directURL.ts`,
// but with much simpler caching logic since this path only allows fetching one path at a time (for now).

import {parseArtifactRef} from '@wandb/weave/core';

import {apolloClient} from '../../../apolloClient';
import {loadArtifactFileDirectUrl} from '../../../files';
import {getUrlWithRetry, NO_RETRY} from '../../../util/requests';

const directURLPromiseByArtifactURI: Map<
  string,
  Promise<string | null>
> = new Map();

export async function fetchDirectURLByArtifactURI(
  uri: string
): ReturnType<typeof fetchDirectURLByArtifactURIUncached> {
  const directURLPromiseFromCache = directURLPromiseByArtifactURI.get(uri);
  if (directURLPromiseFromCache != null) {
    return directURLPromiseFromCache;
  }

  const directURLPromise = fetchDirectURLByArtifactURIUncached(uri);
  directURLPromiseByArtifactURI.set(uri, directURLPromise);

  const directURL = await directURLPromise;
  if (!directURL) {
    directURLPromiseByArtifactURI.delete(uri);
  }

  return directURL;
}

async function fetchDirectURLByArtifactURIUncached(
  uri: string
): Promise<string | null> {
  try {
    const {artifactId, assetPath} = parseArtifactRef(uri);
    const {directUrl: manifestDirectURL} = await loadArtifactFileDirectUrl(
      apolloClient,
      artifactId,
      assetPath
    );
    if (manifestDirectURL == null) {
      return null;
    }

    const manifestString = await getUrlWithRetry(manifestDirectURL, {
      retry: NO_RETRY,
    });
    const manifestJSON = JSON.parse(manifestString);
    if (!manifestJSON.path) {
      return null;
    }

    const {directUrl: mediaDirectURL} = await loadArtifactFileDirectUrl(
      apolloClient,
      artifactId,
      manifestJSON.path
    );
    return mediaDirectURL ?? null;
  } catch (err) {
    console.error(`Error fetching direct URL by artifact URI: ${err}`);
    return null;
  }
}

const WANDB_ARTIFACT_SCHEME = 'wandb-artifact:';
const WANDB_ARTIFACT_CLIENT_SCHEME = 'wandb-client-artifact:';
export function isArtifactRef(uri: string): boolean {
  return (
    uri.startsWith(WANDB_ARTIFACT_SCHEME) ||
    uri.startsWith(WANDB_ARTIFACT_CLIENT_SCHEME)
  );
}
